/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { supabase } from '../lib/supabase';

interface AccountSettingsProps {
  userEmail: string;
  onClose: () => void;
  onLogout: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  color: white;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 24px;
  cursor: pointer;
  
  &:hover {
    color: white;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin-bottom: 4px;
`;

const Input = styled.input`
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: white;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 10px 16px;
  border-radius: 6px;
  border: none;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${props => props.variant === 'danger' 
    ? 'rgba(239, 68, 68, 0.2)' 
    : 'rgba(59, 130, 246, 0.2)'};
  
  color: ${props => props.variant === 'danger' 
    ? '#ef4444' 
    : '#3b82f6'};
    
  &:hover {
    background: ${props => props.variant === 'danger' 
      ? 'rgba(239, 68, 68, 0.3)' 
      : 'rgba(59, 130, 246, 0.3)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  padding: 8px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 6px;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  font-size: 14px;
  padding: 8px;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 6px;
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin: 10px 0;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 18px;
  font-weight: 500;
`;

const NotificationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  backdrop-filter: blur(8px);
`;

const NotificationCard = styled.div`
  background: #1a1a1a;
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
`;

const NotificationTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 20px;
  font-weight: 600;
`;

const NotificationMessage = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
`;

interface UserProfile {
  email: string;
  name: string;
  phone: string;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ userEmail, onClose, onLogout }) => {
  const [profile, setProfile] = useState<UserProfile>({
    email: userEmail,
    name: '',
    phone: ''
  });
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showLogoutNotification, setShowLogoutNotification] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const passwordFormRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        // First try to fetch from user_profiles
        let { data: userProfile, error: userProfileError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', userEmail)
          .single();
          
        if (userProfileError) {
          console.log('Could not find profile in user_profiles, trying profiles table...');
          // If not found in user_profiles, try the profiles table
          const { data: legacyProfile, error: legacyError } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('email', userEmail)
            .single();
            
          if (legacyError) {
            console.error('Error fetching profile from either table:', legacyError);
            return;
          }
          
          userProfile = legacyProfile;
        }
        
        if (userProfile) {
          setProfile({
            email: userEmail,
            name: userProfile.name || '',
            phone: userProfile.phone || ''
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    
    fetchUserProfile();
  }, [userEmail]);
  
  const updateProfile = async () => {
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    
    try {
      // Try to update in both tables to ensure data consistency
      // First, check if profile exists in user_profiles
      const { data: existingUserProfile, error: userProfileFetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', userEmail)
        .single();
        
      // Also check in profiles table
      const { data: existingProfile, error: profileFetchError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('email', userEmail)
        .single();
      
      // Update or insert into user_profiles
      if (existingUserProfile) {
        // Update existing user_profile
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            name: profile.name,
            phone: profile.phone,
            updated_at: new Date()
          })
          .eq('email', userEmail);
          
        if (updateError) {
          console.error('Error updating user_profiles:', updateError);
          setProfileError('Failed to update profile: ' + updateError.message);
          setProfileLoading(false);
          return;
        }
      } else {
        // Try to insert new profile in user_profiles
        // First try to get the user ID
        let userId;
        
        // Look for the ID in profiles table if it exists
        if (existingProfile && existingProfile.id) {
          userId = existingProfile.id;
        } else {
          // Try to get ID from auth.users
          const { data: authData } = await supabase.auth.getUser();
          if (authData && authData.user) {
            userId = authData.user.id;
          }
        }
        
        if (userId) {
          const { error: insertError } = await supabase
            .from('user_profiles')
            .insert({ 
              id: userId,
              email: userEmail,
              name: profile.name,
              phone: profile.phone,
              created_at: new Date(),
              updated_at: new Date()
            });
            
          if (insertError) {
            console.error('Error inserting into user_profiles:', insertError);
          }
        }
      }
      
      // Also update or insert in profiles table for backward compatibility
      if (existingProfile) {
        // Update existing profile
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({ 
            name: profile.name,
            phone: profile.phone,
            updated_at: new Date()
          })
          .eq('email', userEmail);
          
        if (updateError) {
          console.error('Error updating profiles:', updateError);
        }
      } else {
        // Insert into profiles
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({ 
            email: userEmail,
            name: profile.name,
            phone: profile.phone,
            created_at: new Date(),
            updated_at: new Date()
          });
          
        if (insertError) {
          console.error('Error inserting into profiles:', insertError);
        }
      }
      
      setProfileSuccess('Profile updated successfully!');
    } catch (error) {
      setProfileError('An unexpected error occurred');
      console.error('Error updating profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };
  
  const changePassword = async (e: React.FormEvent) => {
    // Always prevent default form submission
    e.preventDefault();
    e.stopPropagation();
    
    if (!newPassword || !confirmPassword) {
      setPasswordError('Please enter both new password and confirmation');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');
    
    try {
      // Update the password with Supabase
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) {
        setPasswordError('Failed to update password: ' + error.message);
      } else {
        // Set success message
        setPasswordSuccess('Password updated successfully!');
        
        // Clear input fields
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        
        // Set the flag to indicate password was changed
        setPasswordChanged(true);
        
        // Show notification after a short delay to ensure UI update happens first
        setTimeout(() => {
          setShowLogoutNotification(true);
        }, 500);
      }
    } catch (error) {
      setPasswordError('An unexpected error occurred');
      console.error('Error changing password:', error);
    } finally {
      setPasswordLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogoutConfirm = async () => {
    try {
      // Close UI
      setShowLogoutNotification(false);
      onClose();
      
      // Set a flag in localStorage to indicate we just changed password
      // This can be used on login page to show a message
      if (passwordChanged) {
        try {
          localStorage.setItem('passwordChanged', 'true');
        } catch (e) {
          console.error('Could not set localStorage flag', e);
        }
      }
      
      // Direct force logout with Supabase
      await supabase.auth.signOut();
      
      // Force page reload to ensure clean state - use replace to prevent back button issues
      window.location.replace('/');
    } catch (error) {
      console.error('Forced logout error:', error);
      // Fallback to parent component's logout if direct method fails
      onLogout();
    }
  };

  const handleDeleteRequest = () => {
    setShowDeleteConfirmation(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true);
      setDeleteError('');

      // Call the RPC function we created in SQL
      const { data, error: deleteError } = await supabase.rpc('handle_delete_account');

      if (deleteError) {
        console.error('Error deleting account:', deleteError);
        setDeleteError(deleteError.message || 'Failed to delete account');
        return;
      }

      if (data === true) {
        // Account successfully deleted
        // Close modals and sign out
        setShowDeleteConfirmation(false);
        onClose();
        onLogout();
      } else {
        setDeleteError('Account deletion failed. Please try again or contact support.');
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setDeleteError('An unexpected error occurred');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <Title>Account Settings</Title>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </ModalHeader>
          
          <ModalBody>
            <Section>
              <SectionTitle>Profile Information</SectionTitle>
              
              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={profile.email} 
                  disabled 
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="name">Full Name</Label>
                <Input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={profile.name} 
                  onChange={handleInputChange} 
                  placeholder="Enter your full name" 
                />
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={profile.phone} 
                  onChange={handleInputChange} 
                  placeholder="Enter your phone number" 
                />
              </FormGroup>
              
              {profileError && <ErrorMessage>{profileError}</ErrorMessage>}
              {profileSuccess && <SuccessMessage>{profileSuccess}</SuccessMessage>}
              
              <Button 
                onClick={updateProfile} 
                disabled={profileLoading}
              >
                {profileLoading ? 'Saving Profile...' : 'Save Profile Information'}
              </Button>
            </Section>
            
            <Divider />
            
            <Section>
              <SectionTitle>Danger Zone</SectionTitle>
              {deleteError && <ErrorMessage>{deleteError}</ErrorMessage>}
              
              {!showDeleteConfirmation ? (
                <Button 
                  variant="danger" 
                  onClick={handleDeleteRequest}
                  disabled={deleteLoading}
                >
                  Delete Account
                </Button>
              ) : (
                <div style={{ 
                  border: '1px solid rgba(239, 68, 68, 0.3)', 
                  borderRadius: '8px', 
                  padding: '16px',
                  backgroundColor: 'rgba(239, 68, 68, 0.05)'
                }}>
                  <p style={{ 
                    color: 'white', 
                    fontSize: '16px', 
                    marginTop: 0,
                    marginBottom: '16px'
                  }}>
                    <strong>Are you sure you want to delete your account?</strong>
                  </p>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.7)', 
                    fontSize: '14px',
                    marginBottom: '16px'
                  }}>
                    This action cannot be undone. All your data will be permanently deleted.
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button 
                      onClick={cancelDelete}
                      disabled={deleteLoading}
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={confirmDelete}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? 'Deleting...' : 'Yes, Delete My Account'}
                    </Button>
                  </div>
                </div>
              )}
            </Section>
          </ModalBody>
        </ModalContainer>
      </ModalOverlay>
      
      {showLogoutNotification && (
        <NotificationOverlay>
          <NotificationCard>
            <NotificationTitle>Password Updated</NotificationTitle>
            <NotificationMessage>
              For security reasons, you will be logged out and need to sign in again with your new password.
            </NotificationMessage>
            <Button onClick={handleLogoutConfirm}>
              Sign Out Now
            </Button>
          </NotificationCard>
        </NotificationOverlay>
      )}
    </>
  );
};

export default AccountSettings; 